import { useEffect, useState } from "react";
import { Activity, Database, HardDrive, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtBytes } from "@/components/OneDriveQuota";

const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 172800) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

const Stat = ({ label, value, sub, icon: Icon, testid }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testid}>
    <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{Icon && <Icon size={11} />}{label}</p>
    <p className="mt-1 font-heading text-2xl font-bold">{value}</p>
    {sub && <p className="text-[11px] text-zinc-500">{sub}</p>}
  </div>
);

const summaryText = (s) => {
  if (!s) return "—";
  const bits = [["added", s.added ?? s.new], ["updated", s.updated ?? s.changed], ["removed", s.removed], ["skipped", s.unchanged ?? s.skipped]].filter(([, v]) => v != null);
  return bits.length ? bits.map(([k, v]) => `${v} ${k}`).join(" · ") : (s.scope ? `scope: ${s.scope}` : "done");
};

/* Owner-only: media cache, database footprint, last scan per source, clear-cache action. */
export const LibraryHealth = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/health").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="flex items-center gap-2 py-10 text-sm text-zinc-500" data-testid="health-loading"><Loader2 size={14} className="animate-spin" />Checking library health…</div>;
  const c = d.cache;
  const lookups = c.hits + c.misses;
  const hitRate = lookups ? Math.round((c.hits / lookups) * 100) : null;
  const pct = c.cap ? Math.min(100, Math.round((c.bytes / c.cap) * 100)) : 0;
  const clear = async () => {
    if (!window.confirm("Clear the media cache? Songs will be re-fetched from FileJump on their next play.")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/cache/clear"); toast.success(`Cleared ${data.cleared} cached file${data.cleared === 1 ? "" : "s"}`); await load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <section className="space-y-5" data-testid="library-health">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="health-cache">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400"><HardDrive size={11} />Media cache</p>
          <p className="mt-1 font-heading text-2xl font-bold">{fmtBytes(c.bytes)}</p>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-100"><div className="h-full rounded-full bg-zinc-900 transition-[width]" style={{ width: `${pct}%` }} /></div>
          <p className="mt-1 text-[11px] text-zinc-500">{c.files} song{c.files === 1 ? "" : "s"} · {pct}% of {fmtBytes(c.cap)}{c.filling ? ` · ${c.filling} filling` : ""}</p>
        </div>
        <Stat label="Hit rate" icon={Activity} value={hitRate == null ? "—" : `${hitRate}%`} sub={lookups ? `${c.hits} hits · ${c.misses} fills · ${c.bypass} bypassed · ${c.warmed} pre-warmed since ${ago(new Date(c.since * 1000).toISOString())}` : "No FileJump plays since the server started"} testid="health-hit-rate" />
        <Stat label="Database" icon={Database} value={fmtBytes(d.db.data_bytes + d.db.index_bytes)} sub={`${d.db.objects.toLocaleString()} records · ${d.db.collections} collections · ${fmtBytes(d.db.index_bytes)} indexes`} testid="health-db" />
        <Stat label="Library" value={`${d.counts.tracks.toLocaleString()} songs`} sub={`${d.counts.albums} albums · ${d.counts.videos} videos · ${d.counts.playlists} playlists`} testid="health-counts" />
      </div>
      <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white" data-testid="health-scans">
        <table className="w-full text-left text-xs">
          <thead><tr className="border-b border-zinc-100 text-[10px] uppercase tracking-[0.15em] text-zinc-400">{["Source", "Last scan", "Result", "Status"].map((h) => <th key={h} className="px-4 py-2 font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {d.scans.length === 0 ? <tr><td colSpan={4} className="px-4 py-5 text-center text-zinc-500">No cloud sources connected</td></tr> : d.scans.map((s) => (
              <tr key={s.source} className="border-b border-zinc-50 last:border-0" data-testid={`health-scan-${s.source.toLowerCase()}`}>
                <td className="px-4 py-2.5 font-medium">{s.source}</td>
                <td className="px-4 py-2.5" title={s.last_synced_at || ""}>{ago(s.last_synced_at)}{s.last_sync_trigger ? <span className="text-zinc-400"> · {s.last_sync_trigger}</span> : null}</td>
                <td className="px-4 py-2.5 text-zinc-600">{summaryText(s.last_sync_summary)}</td>
                <td className="px-4 py-2.5">
                  {s.sync_status === "running" ? <span className="flex items-center gap-1 text-amber-700"><Loader2 size={11} className="animate-spin" />Scanning…</span>
                    : s.sync_status === "error" ? <span className="text-red-700" title={s.sync_error || ""}>Error</span>
                      : <span className="text-emerald-700">OK</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={load} className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-4 text-xs transition-colors hover:border-zinc-900" data-testid="health-refresh"><RefreshCw size={12} />Refresh</button>
        <button onClick={clear} disabled={busy || !c.files} className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-4 text-xs transition-colors hover:border-red-600 hover:text-red-700 disabled:opacity-40" data-testid="health-clear-cache"><Trash2 size={12} />Clear media cache</button>
      </div>
    </section>
  );
};
